import { i18n } from "src/boot/i18n"
import { DEFAULT_CONTEXT_ACTIONS } from "src/util/constants/navigation"
import { ContextAction } from "src/util/enums/entityType"
import { Icons } from "src/util/enums/fontAwesomeIcons"

export class ContextMenuItem {
  private _icon = ""
  private _content = ""
  private _action = ContextAction.HELP
  private _disable = false

  private constructor(item: ContextAction) {
    const itemData = ContextMenuItem.getEnumData(item)
    this._action = item
    this._content = itemData.content
    this._icon = itemData.icon
    this._disable = itemData.disable
  }

  public get icon() {
    return this._icon
  }

  private set icon(icon: string) {
    this._icon = icon
  }

  public get content() {
    return this._content
  }

  private set content(content) {
    this._content = content
  }

  public get action() {
    return this._action
  }

  private set action(contextAction) {
    this._action = contextAction
  }

  public get disable() {
    return this._disable
  }

  public set disable(disabledStatus) {
    this._disable = disabledStatus
  }

  private static getEnumData(item: ContextAction): {
    icon: string
    content: string
    disable: boolean
  } {
    const { t } = i18n.global

    switch (item) {
      case ContextAction.ADD_ATTACHMENT:
        return {
          icon: Icons.ATTACHMENT_ADD,
          content: t("sfxContextMenu.add_attachment"),
          disable: false,
        }

      case ContextAction.ADD_TO_WORKORDER:
        return {
          icon: Icons.WORKORDER_ADD_TO,
          content: t("sfxContextMenu.add_to_work_order"),
          disable: false,
        }

      case ContextAction.ASSOCIATE_IDENTIFIER:
        return {
          icon: Icons.IDENTIFY,
          content: t("sfxContextMenu.associate_identifier"),
          disable: false,
        }

      case ContextAction.CHANGE_REQUEST:
        return {
          icon: Icons.CHANGE_REQUEST,
          content: t("sfxContextMenu.change_requests"),
          disable: false,
        }

      case ContextAction.CLONE_ITEM:
        return {
          icon: Icons.ITEM_CLONE,
          content: t("sfxContextMenu.clone_item"),
          disable: false,
        }

      case ContextAction.CLOSE_ISSUE:
        return {
          icon: Icons.RETIRE,
          content: t("sfxContextMenu.close_issue"),
          disable: false,
        }

      case ContextAction.CREATE_ITEM:
        return {
          icon: Icons.ITEM_CREATE,
          content: t("sfxContextMenu.create_item"),
          disable: false,
        }

      case ContextAction.CREATE_LOCATION:
        return {
          icon: Icons.LOCATION_CREATE,
          content: t("sfxContextMenu.create_location"),
          disable: false,
        }

      case ContextAction.CREATE_MODEL:
        return {
          icon: Icons.MODEL_CREATE,
          content: t("sfxContextMenu.create_model"),
          disable: false,
        }

      case ContextAction.CREATE_WORK_ORDER:
        return {
          icon: Icons.WORKORDER_CREATE,
          content: t("sfxContextMenu.create_work_order"),
          disable: false,
        }

      case ContextAction.CLOSE_WORK_ORDER:
        return {
          icon: Icons.RETIRE,
          content: t("sfxContextMenu.close_work_order"),
          disable: false,
        }

      case ContextAction.DELETE:
        return {
          icon: Icons.DELETE,
          content: t("sfxContextMenu.delete"),
          disable: false,
        }

      case ContextAction.DISCARD:
        return {
          icon: Icons.DELETE,
          content: t("sfxContextMenu.discard"),
          disable: false,
        }

      case ContextAction.DISCARD_WORK:
        return {
          icon: Icons.DELETE,
          content: t("sfxContextMenu.discard_work"),
          disable: false,
        }

      case ContextAction.EXPORT_RESULTS:
        return {
          icon: Icons.S_FILE_EXPORT,
          content: t("sfxContextMenu.export_results"),
          disable: false,
        }

      case ContextAction.FEEDBACK:
        return {
          icon: Icons.FEEDBACK,
          content: t("sfxContextMenu.feedback"),
          disable: false,
        }

      case ContextAction.HANDLE_IDENTIFIERS:
        return {
          icon: Icons.IDENTIFY,
          content: t("sfxContextMenu.handle_identifiers"),
          disable: false,
        }

      case ContextAction.HISTORY:
        return {
          icon: Icons.HISTORY,
          content: t("sfxContextMenu.history"),
          disable: true,
        }

      case ContextAction.ISSUES:
        return {
          icon: Icons.ISSUES,
          content: t("sfxContextMenu.issues"),
          disable: false,
        }

      case ContextAction.MARK_ALL_READ:
        return {
          icon: Icons.MARK_AS_READ,
          content: t("sfxContextMenu.mark_all_read"),
          disable: false,
        }

      case ContextAction.MARK_ALL_UNREAD:
        return {
          icon: Icons.MARK_AS_UNREAD,
          content: t("sfxContextMenu.mark_all_unread"),
          disable: false,
        }

      case ContextAction.PERFORM_ADHOC_WORK:
        return {
          icon: Icons.WORK_REGULAR,
          content: t("sfxContextMenu.perform_adhoc_work"),
          disable: false,
        }

      case ContextAction.PERFORM_CORRECTIVE_WORK:
        return {
          icon: Icons.WORK_CORRECTIVE,
          content: t("sfxContextMenu.perform_corrective_work"),
          disable: false,
        }

      case ContextAction.RAISE_ISSUE:
        return {
          icon: Icons.ISSUE_CREATE,
          content: t("sfxContextMenu.raise_issue"),
          disable: false,
        }

      case ContextAction.DELETE_ISSUE:
        return {
          icon: Icons.DELETE,
          content: t("sfxContextMenu.delete_issue"),
          disable: false,
        }

      case ContextAction.REJECT_ISSUE:
        return {
          icon: Icons.WORK_REJECT,
          content: t("sfxContextMenu.reject_issue"),
          disable: false,
        }

      case ContextAction.REMOVE_FROM_WORKORDER:
        return {
          icon: Icons.WORKORDER_REMOVE_FROM,
          content: t("sfxContextMenu.remove_from_work_order"),
          disable: false,
        }

      case ContextAction.REMOVE_LOCATION:
        return {
          icon: Icons.NO_LOCATIONS,
          content: t("sfxContextMenu.remove_location"),
          disable: false,
        }

      case ContextAction.REPLACE_ITEM:
        return {
          // TODO: Icon missing "replace-item"
          icon: "replace-item",
          content: t("sfxContextMenu.replace_item"),
          disable: false,
        }

      case ContextAction.SUPERSEDE_ISSUE:
        return {
          icon: Icons.ISSUE_SUPERSEDE,
          content: t("sfxContextMenu.supersede_issue"),
          disable: false,
        }

      case ContextAction.SUPPORT:
        return {
          icon: Icons.LOGIN_HELP,
          content: t("sfxContextMenu.support"),
          disable: false,
        }

      case ContextAction.TAKE_PHOTO:
        return {
          icon: Icons.ADD_PHOTO,
          content: t("sfxContextMenu.take_photo"),
          disable: false,
        }

      case ContextAction.TOGGLE_DISPLAY_PERFORMED_ON:
        return {
          icon: Icons.SHOW,
          content: t("sfxContextMenu.toggle_display_performed"),
          disable: false,
        }

      case ContextAction.TOGGLE_DISPLAY_PERFORMED_OFF:
        return {
          icon: Icons.HIDE,
          content: t("sfxContextMenu.toggle_display_performed"),
          disable: false,
        }

      case ContextAction.USER_SETTINGS:
        return {
          icon: Icons.SETTINGS,
          content: t("sfxContextMenu.user_settings"),
          disable: false,
        }

      case ContextAction.IMAGE_ZOOM_OUT:
        return {
          icon: Icons.COMPRESS_GALLERY_IMAGE,
          content: t("sfxContextMenu.image_zoom_out"),
          disable: false,
        }

      case ContextAction.IMAGE_ZOOM_IN:
        return {
          icon: Icons.EXPAND_GALLERY_IMAGE,
          content: t("sfxContextMenu.image_zoom_in"),
          disable: false,
        }

      default:
        return {
          icon: Icons.HELP,
          content: t("sfxContextMenu.help"),
          disable: false,
        }
    }
  }

  /**
   * Returns list of context menu items that correspond the items we have selected to show.
   * If no items are selected, it shows only the default items.
   * Items for disabling that do not have corresponding item to show are ignored.
   *
   * @param itemsToShow List of context menu items we want to get.
   * @param itemsToDisable List of context menu items we want to disable.
   * @param ignoreQuickActionDefault
   */
  public static getContextMenuItems(
    itemsToShow?: ContextAction[],
    itemsToDisable: ContextAction[] = [],
    ignoreQuickActionDefault = false
  ) {
    // If no data is added, add all defaults or empty
    if (!itemsToShow) {
      // Default
      itemsToShow = ignoreQuickActionDefault ? [] : DEFAULT_CONTEXT_ACTIONS
    } else if (!ignoreQuickActionDefault) {
      // Concat the default actions
      for (const defaultAction of DEFAULT_CONTEXT_ACTIONS) {
        if (!itemsToShow.includes(defaultAction)) {
          itemsToShow.push(defaultAction)
        }
      }
    }

    const menuItems: ContextMenuItem[] = []

    for (const item of itemsToShow) {
      const menuItem = new ContextMenuItem(Number(item))

      if (itemsToDisable && itemsToDisable.includes(Number(item))) {
        menuItem.disable = true
      }

      menuItems.push(menuItem)
    }

    return menuItems
  }
}
